import { AnalyticsService } from 'base/analytics';
import { keyOfObject } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { Button } from 'base/ui/button';
import { Rows } from 'base/ui/layout';
import { DividerLine, PanelHeader, PanelScrollable } from 'base/ui/panel';
import { useDebounce } from 'base/useDebounce';
import { observer } from 'mobx-react-lite';
import { computedFn } from 'mobx-utils';
import React, { useEffect } from 'react';
import { globalSectionsInProject } from '../editing_actions/global_sections';
import { ProjectEditingActions } from '../editing_actions/project';
import {
  anInlineSection,
  aPage,
  aReferenceSection,
  Project,
  SectionRefState,
} from '../model';
import { parentsMap } from '../model_helpers';
import { SelectionStore } from '../selection/store';
import { ProjectSession } from '../session/session';
import { AddRoute, SidebarNavigation } from '../sidebar/routes';
import { pageDndDomain } from '../sitemap/page_dnd';
import { commitSuggestion, Suggestion } from '../suggested_edits';
import { AiGenerateHeader } from './ai_generate_header';
import blankSectionImage from './category_images/blank.file.svg';
import { SearchNoResults } from './component_results';
import {
  PageTemplate,
  PAGE_TEMPLATES,
  sectionNameToButtonLabel,
  SECTION_CATEGORIES,
  stripTrailingNumber,
} from './content';
import { fuzzyTextSearch } from './fuzzy_text_search';
import { RemoveSingleSuggestion, SingleSuggestionStore } from './suggestions';
import {
  AddSectionButton,
  CardButtonContent,
  clearSearch,
  GlobalSectionsHeader,
  GotoPageCategoriesRow,
  Search,
} from './ui';

type Category = (typeof SECTION_CATEGORIES)[number];

export const SitemapAddPanel = observer(function _SitemapAddPanel({
  editing,
  selection,
  navigation,
  route,
  analytics,
  session,
  library,
}: {
  editing: ProjectEditingActions;
  selection: SelectionStore;
  navigation: SidebarNavigation;
  route: AddRoute;
  analytics: AnalyticsService;
  session: ProjectSession;
  library: LibraryCache;
}) {
  const [singleSuggestionStore] = React.useState(new SingleSuggestionStore());
  const buttonProps = {
    selection,
    suggestionStore: singleSuggestionStore,
    analytics,
    library,
  };

  const tab =
    route.path === 'add/search'
      ? route.filter.type
      : route.path === 'add/page-categories'
      ? 'pages'
      : 'sections';
  const search = route.path === 'add/search' ? route.search : '';

  const trackDebounced = useDebounce(
    // This needs to be bound otherwise the wrong `this` is used … I love JS "classes"
    analytics.track.bind(analytics),
    1000,
  );

  useEffect(() => {
    if (search === '') {
      return;
    }

    trackDebounced({
      event: 'project:component_search_query_updated',
      query: search,
      component_type: tab,
    });
  }, [search, tab, trackDebounced]);

  const sectionResults = fuzzyTextSearch(
    SECTION_CATEGORIES,
    search,
    (c) => c.name,
  );
  const pageResults = fuzzyTextSearch(PAGE_TEMPLATES, search, (p) => p.name);

  const panelTitle =
    route.path === 'add/page-categories' ? 'Add a Page' : 'Add';

  return (
    <Rows fixedHeight spacing={0}>
      <Rows spacing={12}>
        <PanelHeader title={panelTitle} navigation={navigation} />
        <Search navigation={navigation} route={route} />
        <DividerLine expandToEdges={true} />
      </Rows>

      <PanelScrollable hide={tab !== 'sections'}>
        <Rows spacing={8}>
          {!search && (
            <AiGenerateHeader
              editing={editing}
              session={session}
              selection={selection}
              navigation={navigation}
            />
          )}
          {!search && selection.get().section == null && (
            <GotoPageCategoriesRow navigation={navigation} />
          )}
          <GlobalSectionsHeader {...buttonProps} search={search} />
          <AddSectionButton
            {...buttonProps}
            section={anInlineSection()}
            size="medium"
          >
            <CardButtonContent
              label="Blank Section"
              image={blankSectionImage}
            />
          </AddSectionButton>
          {sectionResults.map((category) => (
            <AddSectionButton
              {...buttonProps}
              key={category.name}
              section={sectionStateForCategory(category)}
            >
              <CardButtonContent
                label={sectionNameToButtonLabel(category.name)}
                image={category.image}
              />
            </AddSectionButton>
          ))}
          {sectionResults.length === 0 && (
            <SearchNoResults
              clearSearch={() => clearSearch(navigation, route)}
              subject="section"
            />
          )}
        </Rows>
      </PanelScrollable>
      <PanelScrollable hide={tab !== 'pages'}>
        <Rows spacing={8}>
          {pageResults.map((category) =>
            category.pages.map((template) => (
              <AddPageButton
                {...buttonProps}
                key={keyOfObject(template)}
                template={template}
              >
                <CardButtonContent
                  label={template.name}
                  image={category.vectorUrl}
                />
              </AddPageButton>
            )),
          )}
          {pageResults.length === 0 && (
            <SearchNoResults
              clearSearch={() => clearSearch(navigation, route)}
              subject="page"
            />
          )}
        </Rows>
      </PanelScrollable>
    </Rows>
  );
});

/**
 * Tries to match the provided section to a existing global section.
 * If no such section exists, or if multiple matches occur, return null.
 */
function findGlobalSection(project: Project, name: string) {
  const globals = globalSectionsInProject(project);
  const matchingIds: string[] = [];
  const search = name.toLowerCase().trim();
  for (const [id, { value }] of globals.entries()) {
    if (value.name.toLowerCase() == search) matchingIds.push(id);
  }
  if (matchingIds.length === 1) {
    return aReferenceSection({ id: matchingIds[0] });
  }
  // If there's more than 1 global, ignore all of them.  (very conservative for a rare case)
  return null;
}

const sectionStateForCategory = computedFn(function (
  category: Category,
): SectionRefState {
  const inline = anInlineSection({
    name: category.name,
    description: category.description || '',
  });
  return inline;
});

const getAddPageSuggestion = computedFn(function (
  selection: SelectionStore,
  template: PageTemplate,
) {
  const sel = selection.get();
  const page = sel.page || selection.project.homePage;

  const addAsSections = page.sections.length === 0;
  const templatePage = aPage({
    name: stripTrailingNumber(template.name),
    sections: template.sections.map((section) => {
      const globalMatch = findGlobalSection(selection.project, section.name);
      if (globalMatch) return globalMatch;

      return anInlineSection({
        name: section.name,
        description: section.description,
        color: section.color,
        // Do not copy the component, because we're not in wireframe mode:
        element: { current: undefined },
        isComponentOutOfSync: false,
      });
    }),
  });

  let suggestion: Suggestion;
  if (addAsSections) {
    suggestion = {
      type: 'add-sections',
      page,
      index: 'end',
      sections: templatePage.sections,
      replaceEmptyPageName: templatePage.name,
    };
  } else {
    const parent = parentsMap(selection.project).get(page) || page;
    const indexInParent = parent.subPages.indexOf(page);
    const index =
      indexInParent >= 0 ? indexInParent + 1 : parent.subPages.length;
    suggestion = { type: 'add-page', parent, index, page: templatePage };
  }
  return { suggestion, templatePage };
});

const AddPageButton = observer(function _AddPageButton({
  children,
  template,
  selection,
  suggestionStore,
  analytics,
  library,
}: {
  children: React.ReactNode;
  template: PageTemplate;
  selection: SelectionStore;
  suggestionStore: SingleSuggestionStore;
  analytics: AnalyticsService;
  library: LibraryCache;
}) {
  const removeMySuggestion = React.useRef<RemoveSingleSuggestion>();
  const { suggestion, templatePage } = getAddPageSuggestion(
    selection,
    template,
  );

  const onClick = () => {
    suggestionStore.clear();
    commitSuggestion(suggestion, selection, library);

    trackAdded();
  };
  const trackAdded = () => {
    analytics.track({
      event: 'blocks/add-page',
      method: 'result',
      name: template.name,
    });
  };

  const onMouseEnter = () => {
    removeMySuggestion.current = suggestionStore.set(suggestion);
  };
  const onMouseLeave = () => {
    // a delay to reduce flickering when mousing between buttons
    removeMySuggestion.current?.({ delayMs: 100 });
  };
  React.useEffect(() => () => removeMySuggestion.current?.(), []);

  return (
    <pageDndDomain.Draggable
      source={{ page: templatePage }}
      onDragStart={onMouseLeave}
      onAfterDrop={trackAdded}
    >
      <Button
        onClick={onClick}
        fullWidth
        variant="secondary"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        size="large"
      >
        {children}
      </Button>
    </pageDndDomain.Draggable>
  );
});
